import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { TicketModel, ParlayTicketModel, CricketPlayerBetTypeList, useTicketStatus, useParlayStatus, AcceptTicketStatus, BETTING_CODE } from '@/hooks/query';
import { ConfirmBetTicketHeader } from '../confirm-bet-ticket-header';
import { TicketChoice, SportBetTypeName, Score, TeamName, CricketPlayerName, OddsInfo } from '@/features/betslip/betslip-container/components/bet-ticket';
import { BetTicketOddsGroup } from '@/features/betslip/betslip-container/components/bet-ticket-odds-group';
import { useBetslipStore } from '@/store/betslip';
import { ConfirmBetTicketStatus } from '../confirm-bet-ticket-status';
import { addCommasWithFix } from '@/utils/odds-utils';
import { TextError } from '@/features/betslip/betslip-container/components/bet-msg';
import { useTranslations } from 'next-intl';
import { BetslipService } from '@/services/betslip.service';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { SingleMatchParlayTicketDetails } from '@/features/single-match-parlay/components/single-match-parlay-ticket-details';
import { matchSome } from '@nf/utils-common/fpw/monadic';
import { confirmBetStyle } from '../../confirm-bet.style';

interface ConfirmBetTicketProps {
	isParlay: boolean;
	tickets: Array<TicketModel>;
	comboData?: Array<ParlayTicketModel>;
	transId: string;
};

// 因為後端回傳的waiting偶爾會錯字導致比對失誤...所以將錯字也加進來...
const waitingStatus: string[] = ['waiting', 'waitting'];

export const ConfirmBetTicket = ({
	isParlay,
	tickets,
	comboData,
	transId
}: ConfirmBetTicketProps) => {
	const [timeoutKey, setTimeoutKey] = useState(0);
	const [status, setStatus] = useState<Array<AcceptTicketStatus>>([]);
	const t = useTranslations('General');

	const { oddsType } = useBetslipContext();
	const { refetch: refetchTicketStatus, ticketStatus } = useTicketStatus(transId);
	const { refetch: refetchParlayStatus, parlayStatus } = useParlayStatus(transId);

	const [
		updateMultiTicket,
		updateParlayItemTicket
	] = useBetslipStore(state => [
		state.updateMultiTicket,
		state.updateParlayItemTicket
	]);

	useEffect(() => {
		const initStatus: Array<AcceptTicketStatus> = tickets.map(ticket => {
			if (isParlay) {
				if ([-99].includes(ticket.Code)) {
					return ticket.CheckWaitingTicket ? 'waiting' : 'running';
				} else {
					return 'reject';
				}
			} else {
				if (([BETTING_CODE.Waiting, BETTING_CODE.Accepted] as Array<number>).includes(ticket.ErrorCode)) {
					return ticket.CheckWaitingTicket ? 'waiting' : 'running';
				} else {
					return 'reject';
				}
			}
		});

		setStatus(initStatus);
	}, []);

	const getPayoutTitle = (type: '' | 'profit' | 'liability'): string => {
		let title;

		switch (type) {
			case 'profit':
				title = t('lbl_Crkt_Profit');
				break;
			case 'liability':
				title = t('lbl_Crkt_Liability');
				break;
			default:
				title = t('lbl_payout');
				break;
		}
		return title;
	}

	if (!isParlay && tickets[0].CheckWaitingTicket) {
		// 單票下注, 票的狀態還是waiting時每三秒查詢一次狀態
		if (!ticketStatus || waitingStatus.includes(ticketStatus?.status)) {

			if (!timeoutKey) {
				const key = window.setTimeout(() => {
					refetchTicketStatus();
					setTimeoutKey(0);
				}, 3000);

				setTimeoutKey(key);
			}
			// 從api取得回傳值非waiting時, 將api回傳值塞回status(僅執行一次), 否則會持續觸發render
		} else if (!waitingStatus.includes(ticketStatus?.status) && status.some(stat => waitingStatus.includes(stat))) {

			// 如果api回傳reject, 要將reject緣由塞回ticket, 讓前端能夠顯示
			if (ticketStatus.status.toLowerCase() === 'reject') {
				const errorMessage = ticketStatus.ReasonID ? t(`rr_${ticketStatus.ReasonID}`) : t('rr_601');
				const errorCode = BETTING_CODE.RejectTicket;
				updateMultiTicket({
					...tickets[0],
					ErrorCode: errorCode,
					Message: errorMessage,
					Common: {
						ErrorCode: errorCode,
						ErrorMsg: errorMessage
					}
				}, oddsType);
			}

			setStatus([ticketStatus.status.toLowerCase() as AcceptTicketStatus]);
		}
		// 串關下注, 只要有場次還是waiting時每三秒查詢一次狀態
	} else if (isParlay && tickets.some(ticket => ticket.CheckWaitingTicket)) {
		const hasWaiting = !parlayStatus || parlayStatus?.Slave.some(parlaySubTicket => waitingStatus.includes(parlaySubTicket.Status));

		if (hasWaiting) {

			if (!timeoutKey) {
				const key = window.setTimeout(() => {
					refetchParlayStatus();
					setTimeoutKey(0);
				}, 3000);

				setTimeoutKey(key);
			}
			// 從api取得回傳值 "皆" 非waiting時, 將api回傳值塞回status(僅執行一次), 否則會持續觸發render
		} else if (parlayStatus.Slave.every(parlaySubTicket => !waitingStatus.includes(parlaySubTicket.Status)) &&
			status.some(stat => waitingStatus.includes(stat))) {

			const newStatus = parlayStatus.Slave.reduce((result, parlaySubTicket) => {
				const index = tickets.findIndex(ticket => ticket.Matchid == parlaySubTicket.MatchId);

				result[index] = parlaySubTicket.Status.toLowerCase() as AcceptTicketStatus;

				return result;
			}, new Array<AcceptTicketStatus>());

			// 如果api回傳有任何的reject, 要將reject緣由塞回ticket, 讓前端能夠顯示
			parlayStatus.Slave.forEach(parlaySubTicket => {
				if (parlaySubTicket.Status.toLowerCase() === 'reject') {
					const errorMessage = parlaySubTicket.ReasonIdm ? t(`rr_${parlaySubTicket.ReasonIdm}`) : t('rr_601');
					const errorCode = BETTING_CODE.RejectTicket;
					updateParlayItemTicket({
						...tickets[0],
						ErrorCode: errorCode,
						Code: errorCode,
						Message: errorMessage,
						Common: {
							ErrorCode: errorCode,
							ErrorMsg: errorMessage
						}
					});
				}
			});

			setStatus(newStatus);
		}
	}

	const totalStake = isParlay && comboData ?
		comboData.reduce((subTotalStake, parlayTicket) => {
			return subTotalStake + parlayTicket.TotalStake;
		}, 0) :
		tickets[0].stake;

	const totalPayout = isParlay && comboData ?
		comboData?.reduce((subTotalPayout, parlayTicket) => {
			return subTotalPayout + parlayTicket.MaxPayout;
		}, 0) : tickets[0].payout;

	const transStatus: AcceptTicketStatus = matchSome(status)
		.on((status: AcceptTicketStatus[]) => status.some(stat => stat == 'reject'), (): AcceptTicketStatus => 'reject')
		.on((status: AcceptTicketStatus[]) => status.some(stat => waitingStatus.includes(stat)), (): AcceptTicketStatus => 'waiting')
		.on((status: AcceptTicketStatus[]) => status.every(stat => stat == 'running'), (): AcceptTicketStatus => 'running')
		.otherwise((): AcceptTicketStatus => 'reject');

	return (
		<>
			{
				isParlay ?
					<ConfirmBetTicketHeader
						key={'parlay'}
						isParlay={true}
						tickets={tickets}
						parlayTickets={comboData?.filter(parlayTicket => parlayTicket.stake > 0)}
						status={transStatus}
					/> :
					<ConfirmBetTicketHeader
						key={tickets[0].OddsID}
						isParlay={false}
						tickets={[tickets[0]]}
						status={transStatus}
					/>
			}
			{
				tickets.map((ticket, index) => {
					return (
						<div key={ticket.OddsID} className={clsx('ticket', confirmBetStyle.ticket)}>
							<div className="ticket__wrap">
								<div className="ticket__status">
									{
										isParlay ?
											<ConfirmBetTicketStatus status={status[index]} /> :
											null
									}
								</div>
								<div className="ticket__container">
									<div className="ticket__header">
										<TicketChoice ticket={ticket} />
									</div>
									<div className="ticket__row">
										<SportBetTypeName ticket={ticket} />
										<Score ticket={ticket} />
									</div>
									<TeamName ticket={ticket} />
									<div className="ticket__row">
										<SingleMatchParlayTicketDetails choices={ticket.specialChoices ?? []} />
									</div>
									
									{
										CricketPlayerBetTypeList.includes(Number(ticket.Bettype))
											? <CricketPlayerName ticket={ticket} />
											: <OddsInfo ticket={ticket} />
									}
									{
										status[index] === 'reject' && ![7, 8, 56].includes(ticket.Code) && ticket.Message
											? <TextError msg={ticket.Message} />
											: null
									}
									{
										ticket.ACCode ? 
											<div className="ticket__row">
												<div className="text-anti-counterfeiting-code">{ticket.ACCode}</div> 
											</div>: null
									}
								</div>
								<BetTicketOddsGroup
									ticket={ticket}
									isParlayConfirmBet={isParlay}
								/>
							</div>
						</div>
					)
				})
			}
			{
				status.every(status => status != 'reject') ?
					<div className={clsx('betting-info', confirmBetStyle.bettingInfo)}>
						{
							transId && transId != '0' ?
								<div className={clsx('ticket-id', confirmBetStyle.ticketId)}>{`${t('lbl_TicketID')} : ${transId}`}</div>
								: null
						}
						<div className="betting-info__group">
							<div className={clsx('betting-info__label', confirmBetStyle.label)}>{t('lbl_stake')}</div>
							<div className={clsx('betting-info__amount', confirmBetStyle.amount, confirmBetStyle.amountSize)}>
								{addCommasWithFix(totalStake, 0)}
							</div>
						</div>
						<div className="betting-info__group">
							<div className={clsx('betting-info__label', confirmBetStyle.label)}>
								{
									isParlay ?
										t('lbl_payout') :
										getPayoutTitle(BetslipService.getPayoutType(tickets[0].Bettype, tickets[0].Betteam))
								}
							</div>
							<div className={clsx('betting-info__amount', confirmBetStyle.amount, confirmBetStyle.amountSize)}>
								{addCommasWithFix(totalPayout, 2)}
							</div>
						</div>
					</div> : null
			}
		</>
	);
}